// src/components/TalentRepeater.tsx
'use client';

import React, { useEffect, useState } from 'react';
import TalentCard, { TalentCardProps } from '@/components/TalentCard';
import TalentCardSkeleton from '@/components/skeletons/TalentCard';
// NO CLIENT-SIDE GATING — the API picks the correct avatar URL per viewer.
// See: docs/CONTENT-GATING-SPEC.md.

export interface TalentRepeaterProps {
  /** New prop name */
  items?: TalentCardProps[] | null;
  /** Back-compat: older pages still pass `people` */
  people?: TalentCardProps[] | null;
  loading?: boolean;
  placeholderCount?: number;
  className?: string;
  /** override grid template classes (columns) */
  gridClassName?: string;
  /** optional override for the empty state */
  emptyState?: React.ReactNode;
  /** suppress rendering when empty */
  suppressEmpty?: boolean;
  /** visual variant of TalentCard */
  variant?: 'default' | 'legacy';
  /** limit total rendered items */
  limit?: number;
  /** compact mode removes vertical padding to sit flush with neighbors */
  compact?: boolean;
}

export default function TalentRepeater({
  items,
  people,
  loading = false,
  placeholderCount = 4,
  className = '',
  gridClassName,
  emptyState,
  suppressEmpty = false,
  variant = 'default',
  limit,
  compact = false,
}: TalentRepeaterProps) {
  // Responsive skeleton column count
  const [cols, setCols] = useState(4);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq1 = window.matchMedia('(min-width: 1280px)');
    const mq2 = window.matchMedia('(min-width: 768px)');
    const mq3 = window.matchMedia('(min-width: 640px)');
    const onChange = () => setCols(mq1.matches ? 6 : (mq2.matches ? 4 : (mq3.matches ? 2 : 1)));
    try { onChange(); mq1.addEventListener('change', onChange); mq2.addEventListener('change', onChange); mq3.addEventListener('change', onChange); }
    catch { mq1.addListener(onChange as any); mq2.addListener(onChange as any); mq3.addListener(onChange as any); }
    return () => { try { mq1.removeEventListener('change', onChange); mq2.removeEventListener('change', onChange); mq3.removeEventListener('change', onChange); }
      catch { mq1.removeListener(onChange as any); mq2.removeListener(onChange as any); mq3.removeListener(onChange as any); } };
  }, []);
  // Known-issue banners removed; show plain empty text only
  // Use whichever prop was provided; guard to an array
  const list: TalentCardProps[] = Array.isArray(items)
    ? items
    : Array.isArray(people)
    ? people
    : [];

  const effectivePlaceholderCount = limit && limit > 0 ? Math.min(placeholderCount, limit) : placeholderCount;

  const baseGrid = gridClassName || 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-6 gap-y-4 gap-x-5 px-0';

  if (loading) {
    const count = typeof effectivePlaceholderCount === 'number' && effectivePlaceholderCount > 0 ? effectivePlaceholderCount : cols;
    return (
      <div className={`${baseGrid} ${compact ? 'py-0' : 'py-4'} ${className}`}>
        {Array.from({ length: count }).map((_, i) => (
          <TalentCardSkeleton key={i} variant={variant} />
        ))}
      </div>
    );
  }

  if (list.length === 0) {
    if (suppressEmpty) return null;
    return emptyState ? (
      <div className={className}>{emptyState}</div>
    ) : (
      <p className={`text-sm text-gray-500 ${className}`} aria-live="polite">No talent to show here yet.</p>
    );
  }

  const renderList = limit && limit > 0 ? list.slice(0, limit) : list;

  return (
    <div className={`${baseGrid} ${compact ? 'py-0' : 'py-4'} ${className}`}>
      {renderList.map((t, idx) => (
        <TalentCard
          // Prefer a stable id when present; fall back to idx
          key={(t as any).id ?? (t as any).personId ?? idx}
          {...t}
          variant={variant}
        />
      ))}
    </div>
  );
}
